import React from 'react';
import { Inbox, type LucideIcon } from 'lucide-react';
import { cn } from '../lib/utils';

export interface EmptyStateProps {
  variant?: 'default' | 'compact';
  icon?: LucideIcon;
  title?: string;
  message?: string;
  description?: string;
  action?: string;
  actionLabel?: string;
  actions?: React.ReactNode;
  onAction?: () => void;
  className?: string;
}

export function EmptyState({
  variant = 'default',
  icon: Icon = Inbox,
  title,
  message,
  description,
  action,
  actionLabel,
  actions,
  onAction,
  className,
}: EmptyStateProps) {
  const displayMessage = message || description;
  const displayAction = action || actionLabel;

  return (
    <div
      className={cn(
        variant === 'compact'
          ? 'flex flex-wrap items-center gap-2 px-4 py-3 text-left'
          : 'relative flex flex-col items-center justify-center overflow-hidden rounded-card-comfort border border-border bg-surface-sheet p-7 sm:py-10 text-center',
        className,
      )}
    >
      {variant !== 'compact' ? (
        <div className="flex h-12 w-12 items-center justify-center rounded-control border border-statura-green-accessible/15 bg-statura-green-accessible/10">
          <Icon className="h-6 w-6 text-statura-green-accessible" aria-hidden="true" />
        </div>
      ) : null}
      {title && (
        <p
          className={
            variant === 'compact'
              ? 'text-sm font-semibold text-text-heading'
              : 'mt-4 max-w-xl font-heading text-card-title text-text-heading'
          }
        >
          {title}
        </p>
      )}
      {displayMessage && (
        <p
          className={cn(
            variant === 'compact'
              ? 'text-sm text-text-muted'
              : 'max-w-2xl font-body text-body text-text-body',
            variant !== 'compact' && (title ? 'mt-2' : 'mt-4'),
          )}
        >
          {displayMessage}
        </p>
      )}
      {actions && (
        <div
          className={
            variant === 'compact'
              ? 'flex flex-wrap gap-2'
              : 'mt-5 flex flex-wrap justify-center gap-2'
          }
        >
          {actions}
        </div>
      )}
      {!actions && displayAction && onAction && (
        <button
          onClick={onAction}
          className={cn(
            'mt-5 inline-flex min-h-control items-center justify-center rounded-control border border-statura-green-accessible bg-statura-green-accessible px-4 py-2 font-body text-body font-semibold text-text-inverse transition-interactive motion-reduce:transition-none duration-200 hover:bg-statura-green-accessible-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible focus-visible:ring-offset-2',
            variant === 'compact' && 'mt-0 min-h-9 px-3 py-1 text-sm',
          )}
        >
          {displayAction}
        </button>
      )}
    </div>
  );
}
